/* eslint-disable camelcase */

import {
  saveCustomerRequest,
  saveCustomerSuccess,
  showErrorAlert,
  showSuccessAlert,
} from 'actions';
import {
  fetchAllDashboards,
  upsertDashboardFailure,
  upsertDashboardRequest,
  upsertDashboardSuccess,
} from 'actions/dashboard';
import {
  addAllValuesToField,
  addAndRemoveValuesToTree,
  addFieldsToGroup,
  addValuesToGroup,
  addValueToTree,
  changeConditionalByFieldValue,
  changeConditionalType,
  clearSelectedFields,
  clearSelectedFilters,
  exitEditWidgetMode,
  removeFieldFromTree,
  removeFieldsFromGroup,
  removeValueFromTree,
  removeValuesFromGroup,
  removeValuesFromTree,
  saveWidget,
  saveWidgetCustomerConfig,
  setFilterSearch,
  setTree,
} from 'actions/dashboardEdit';
import { apiClient as LitLingoClient } from 'client';

import { andFilters } from 'constants/filtersMappingValues';
import { cloneDeep } from 'lodash';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { getUserCustomer } from 'selectors/auth';
import { getActiveDashboardConfig } from 'selectors/dashboard';
import { getEditWidget, getEditWidgetIndex, getTree } from 'selectors/dashboardEdit';
import type { API, Customer, Dashboard, DashboardWidget, SagaReturn } from 'types';

import {
  addAllValuesToField as addAllValuesToFieldAction,
  addFieldToGroup,
  addToGroup,
  changeConditional,
  changeConditionalByField,
  findFieldFromValue,
  removeField,
  removeFieldFromGroup,
  removeFields,
  removeFromGroup,
  removeValue,
  setNewValue,
  transformToString,
  Tree,
} from 'utils/parserTree';

function* addAndRemoveValuesToTreeSaga(
  action: ReturnType<typeof addAndRemoveValuesToTree>
): SagaReturn {
  const { valuesToAdd, fieldsToRemove, metricIndex } = action.payload;

  try {
    const tree = cloneDeep((yield select((state) => getTree(state, metricIndex || 0))) as Tree);

    let newTree = tree;
    valuesToAdd.forEach((valueToAdd) => {
      newTree = setNewValue(
        newTree,
        valueToAdd.field,
        valueToAdd.value,
        valueToAdd.label,
        valueToAdd.isSingleValue
      );
    });

    newTree = removeFields(newTree, fieldsToRemove);

    yield put(setFilterSearch({ filterSearch: transformToString(newTree), metricIndex }));
    yield put(setTree({ tree: newTree, metricIndex }));
  } catch (e) {
    yield put(showErrorAlert((e as Error).message));
  }
}

function* addNewValueToTreeSaga(action: ReturnType<typeof addValueToTree>): SagaReturn {
  const { payload } = action;

  try {
    const tree = cloneDeep(
      (yield select((state) => getTree(state, payload.metricIndex || 0))) as Tree
    );

    const newTree = setNewValue(
      tree,
      payload.field,
      payload.value,
      payload.label,
      payload.isSingleValue
    );

    yield put(
      setFilterSearch({
        filterSearch: transformToString(newTree),
        metricIndex: payload.metricIndex,
      })
    );
    yield put(setTree({ tree: newTree, metricIndex: payload.metricIndex }));
  } catch (e) {
    showErrorAlert((e as Error).message);
  }
}

function* removeValueFromTreeSaga(action: ReturnType<typeof removeValueFromTree>): SagaReturn {
  const { payload } = action;
  try {
    const tree = cloneDeep(
      (yield select((state) => getTree(state, payload.metricIndex || 0))) as Tree
    );

    const newTree = removeValue(tree, payload.value, payload.field, payload.fieldId);

    yield put(
      setFilterSearch({
        filterSearch: transformToString(newTree),
        metricIndex: payload.metricIndex,
      })
    );
    yield put(setTree({ tree: newTree, metricIndex: payload.metricIndex }));
  } catch (e) {
    showErrorAlert((e as Error).message);
  }
}

function* removeValuesFromTreeSaga(action: ReturnType<typeof removeValuesFromTree>): SagaReturn {
  const { payload } = action;
  try {
    const tree = cloneDeep(
      (yield select((state) => getTree(state, payload.metricIndex || 0))) as Tree
    );

    let newTree = tree;

    payload.values.forEach((v) => {
      newTree = removeValue(tree, v, payload.field);
    });

    yield put(
      setFilterSearch({
        filterSearch: transformToString(newTree),
        metricIndex: payload.metricIndex,
      })
    );
    yield put(setTree({ tree: newTree, metricIndex: payload.metricIndex }));
  } catch (e) {
    showErrorAlert((e as Error).message);
  }
}

function* changeConditionalSaga(action: ReturnType<typeof changeConditionalType>): SagaReturn {
  const { payload } = action;

  try {
    const tree = cloneDeep(
      (yield select((state) => getTree(state, payload.metricIndex || 0))) as Tree
    );

    const newTree = changeConditional(tree, payload.node, payload.condition);

    yield put(
      setFilterSearch({
        filterSearch: transformToString(newTree),
        metricIndex: payload.metricIndex,
      })
    );
    yield put(setTree({ tree: newTree, metricIndex: payload.metricIndex }));
  } catch (e) {
    showErrorAlert((e as Error).message);
  }
}

function* changeConditionalByFieldSaga(
  action: ReturnType<typeof changeConditionalByFieldValue>
): SagaReturn {
  const { payload } = action;

  try {
    const tree = cloneDeep(
      (yield select((state) => getTree(state, payload.metricIndex || 0))) as Tree
    );

    const newTree = changeConditionalByField(tree, payload.field, payload.condition);

    yield put(
      setFilterSearch({
        filterSearch: transformToString(newTree),
        metricIndex: payload.metricIndex,
      })
    );
    yield put(setTree({ tree: newTree, metricIndex: payload.metricIndex }));
  } catch (e) {
    showErrorAlert((e as Error).message);
  }
}

function* addValuesToGroupSaga(action: ReturnType<typeof addValuesToGroup>): SagaReturn {
  const { payload } = action;

  try {
    const tree = cloneDeep(
      (yield select((state) => getTree(state, payload.metricIndex || 0))) as Tree
    );

    const field = findFieldFromValue(tree, payload.nodes[0].value);
    if (!field.length) return;
    if (!andFilters.includes(`${field}_and`)) return;
    const newTree = addToGroup(tree, payload.nodes);

    yield put(
      setFilterSearch({
        filterSearch: transformToString(newTree),
        metricIndex: payload.metricIndex,
      })
    );
    yield put(setTree({ tree: newTree, metricIndex: payload.metricIndex }));
    yield put(clearSelectedFilters());
  } catch (e) {
    yield put(clearSelectedFilters());
    showErrorAlert((e as Error).message);
  }
}

function* removeValuesFromGroupSaga(action: ReturnType<typeof removeValuesFromGroup>): SagaReturn {
  const { payload } = action;

  try {
    const tree = cloneDeep(
      (yield select((state) => getTree(state, payload.metricIndex || 0))) as Tree
    );

    const newTree = removeFromGroup(tree, payload.nodes);

    yield put(
      setFilterSearch({
        filterSearch: transformToString(newTree),
        metricIndex: payload.metricIndex,
      })
    );
    yield put(setTree({ tree: newTree, metricIndex: payload.metricIndex }));
    yield put(clearSelectedFilters());
  } catch (e) {
    yield put(clearSelectedFilters());
    showErrorAlert((e as Error).message);
  }
}

function* addFieldsToGroupSaga(action: ReturnType<typeof addFieldsToGroup>): SagaReturn {
  const { payload } = action;

  try {
    const tree = cloneDeep(
      (yield select((state) => getTree(state, payload.metricIndex || 0))) as Tree
    );

    const newTree = addFieldToGroup(tree, payload.nodes);

    yield put(
      setFilterSearch({
        filterSearch: transformToString(newTree),
        metricIndex: payload.metricIndex,
      })
    );
    yield put(setTree({ tree: newTree, metricIndex: payload.metricIndex }));
    yield put(clearSelectedFields());
  } catch (e) {
    yield put(clearSelectedFields());
    yield put(showErrorAlert((e as Error).message));
  }
}

function* removeFieldsFromGroupSaga(action: ReturnType<typeof removeFieldsFromGroup>): SagaReturn {
  const { payload } = action;

  try {
    const tree = cloneDeep(
      (yield select((state) => getTree(state, payload.metricIndex || 0))) as Tree
    );

    const newTree = removeFieldFromGroup(tree, payload.nodes);

    yield put(
      setFilterSearch({
        filterSearch: transformToString(newTree),
        metricIndex: payload.metricIndex,
      })
    );
    yield put(setTree({ tree: newTree, metricIndex: payload.metricIndex }));
    yield put(clearSelectedFields());
  } catch (e) {
    yield put(clearSelectedFields());
    yield put(showErrorAlert((e as Error).message));
  }
}

function* removeFieldFromTreeSaga(action: ReturnType<typeof removeFieldFromTree>): SagaReturn {
  const { payload } = action;

  try {
    const tree = cloneDeep(
      (yield select((state) => getTree(state, payload.metricIndex || 0))) as Tree
    );

    const newTree = removeField(tree, payload.field, payload.id);

    yield put(
      setFilterSearch({
        filterSearch: transformToString(newTree),
        metricIndex: payload.metricIndex,
      })
    );
    yield put(setTree({ tree: newTree, metricIndex: payload.metricIndex }));
  } catch (e) {
    showErrorAlert((e as Error).message);
  }
}

function* addAllValuesToFieldSaga(action: ReturnType<typeof addAllValuesToField>): SagaReturn {
  const { payload } = action;

  try {
    const tree = cloneDeep(
      (yield select((state) => getTree(state, payload.metricIndex || 0))) as Tree
    );

    const newTree = addAllValuesToFieldAction(tree, payload.nodes, payload.field);

    yield put(
      setFilterSearch({
        filterSearch: transformToString(newTree),
        metricIndex: payload.metricIndex,
      })
    );
    yield put(setTree({ tree: newTree, metricIndex: payload.metricIndex }));
  } catch (e) {
    showErrorAlert((e as Error).message);
  }
}

function* saveWidgetSaga({ payload }: ReturnType<typeof saveWidget>): SagaReturn {
  const { multimetric } = payload;

  let widget = (yield select(getEditWidget)) as DashboardWidget;
  const widgetIndex = (yield select(getEditWidgetIndex)) as number;
  const dashboard = (yield select(getActiveDashboardConfig)) as Dashboard;

  widget = {
    ...widget,
    metrics: widget.metrics?.map((m, idx) => ({
      ...m,
      filters_search:
        // @ts-expect-error
        widget.filters_search[idx] !== null && multimetric
          ? // @ts-expect-error
            widget.filters_search[idx]
          : // @ts-expect-error
            widget.filters_search[0],
    })),
  };

  if (dashboard) {
    const { uuid, name, type = 'standard', allowed_roles: allowedRoles, config } = dashboard;

    const newDashboard = {
      uuid,
      name,
      type,
      allowed_roles: allowedRoles,
      config: {
        ...config,
        widgets: config?.widgets.map((w, idx) => (idx === widgetIndex ? widget : w)),
      },
    };

    yield put(upsertDashboardRequest(dashboard));

    const response = (yield call([LitLingoClient.resources.dashboards, 'upsert'], {
      data: newDashboard,
    })) as API.Response<API.Dashboards.Upsert>;
    if (response.error != null) {
      yield put(upsertDashboardFailure(response.error));
    } else {
      yield put(showSuccessAlert('Widget saved.'));
      yield put(upsertDashboardSuccess(response.data));
      yield put(fetchAllDashboards());

      yield put(exitEditWidgetMode());
    }
  }
}

function* saveWidgetCustomerDashboardSaga({
  payload,
}: ReturnType<typeof saveWidgetCustomerConfig>): SagaReturn {
  const { multimetric } = payload;

  let widget = (yield select(getEditWidget)) as DashboardWidget;
  const widgetIndex = (yield select(getEditWidgetIndex)) as number;
  const customer = (yield select(getUserCustomer)) as Customer;
  const dashboardConfig = customer.config.dashboard_config;

  widget = {
    ...widget,
    metrics: widget.metrics?.map((m, idx) => ({
      ...m,
      filters_search:
        // @ts-expect-error
        widget.filters_search[idx] !== null && multimetric
          ? // @ts-expect-error
            widget.filters_search[idx]
          : // @ts-expect-error
            widget.filters_search[0],
    })),
  };

  if (dashboardConfig) {
    const newDashboardConfig = {
      ...dashboardConfig,
      widgets: dashboardConfig?.widgets.map((w, idx) => (idx === widgetIndex ? widget : w)),
    };

    yield put(saveCustomerRequest());

    const response = (yield call([LitLingoClient.resources.customers, 'upsert'], {
      data: { ...customer, config: { ...customer.config, dashboard_config: newDashboardConfig } },
    })) as API.Response<API.Customers.Upsert>;
    if (response.error != null) {
      yield put(upsertDashboardFailure(response.error));
    } else {
      yield put(showSuccessAlert('Widget saved.'));
      yield put(saveCustomerSuccess(response.data));
      yield put(fetchAllDashboards());
      yield put(exitEditWidgetMode());
    }
  }
}

function* dashboardEditSaga(): SagaReturn {
  yield takeLatest(addValueToTree.toString(), addNewValueToTreeSaga);
  yield takeLatest(removeValueFromTree.toString(), removeValueFromTreeSaga);
  yield takeLatest(removeValuesFromTree.toString(), removeValuesFromTreeSaga);
  yield takeLatest(addAndRemoveValuesToTree.toString(), addAndRemoveValuesToTreeSaga);
  yield takeLatest(changeConditionalType.toString(), changeConditionalSaga);
  yield takeLatest(addValuesToGroup.toString(), addValuesToGroupSaga);
  yield takeLatest(removeValuesFromGroup.toString(), removeValuesFromGroupSaga);
  yield takeLatest(addFieldsToGroup.toString(), addFieldsToGroupSaga);
  yield takeLatest(removeFieldsFromGroup.toString(), removeFieldsFromGroupSaga);
  yield takeLatest(removeFieldFromTree.toString(), removeFieldFromTreeSaga);
  yield takeLatest(addAllValuesToField.toString(), addAllValuesToFieldSaga);
  yield takeLatest(changeConditionalByFieldValue.toString(), changeConditionalByFieldSaga);
  yield takeLatest(saveWidget.toString(), saveWidgetSaga);
  yield takeLatest(saveWidgetCustomerConfig.toString(), saveWidgetCustomerDashboardSaga);
}

export default dashboardEditSaga;
